/**
 * The public site's country registry (ADR-0030 / ADR-0032). Which countries the platform serves and, per
 * country, its locale config (recommended default + served locales). NEUTRAL by construction: this file holds
 * **no** country/locale literal — the registry is supplied at runtime (the edge/platform injects it, sourced
 * from the country-config store, `GET /v1/config/countries/:cc/resolve`), so the site never hardcodes a market
 * (ADR-0013 neutrality gate). Country names render through `@vello/i18n` message ids, never inline copy.
 */
import type { CountryLocaleConfig } from '@vello/web-entry';

export interface SiteCountry {
  /** ISO-3166-1 alpha-2, upper-case. The localized display NAME is rendered at runtime via `Intl.DisplayNames`
   *  (locale-aware, no hardcoded country name — keeps the site neutral), never stored here. */
  readonly code: string;
  /** The country's locale config — recommended default + served locales. */
  readonly locales: CountryLocaleConfig;
}

export interface SiteRegistry {
  readonly countries: readonly SiteCountry[];
}

/** One country as returned by the platform's `GET /v1/config/countries` (the wire shape the edge injects). */
export interface CountryConfigDTO {
  readonly country: string;
  readonly locales: { readonly default: string; readonly supported: readonly string[] };
  readonly capabilities?: { readonly product: readonly string[]; readonly payment: readonly string[] };
  readonly currency?: string;
}

/**
 * Build the site registry from the platform's served-country list (`GET /v1/config/countries`). Pure, so the
 * edge injector (or a dev harness) maps the live response into `window.__VELLO_SITE_CONFIG__` deterministically.
 * Only the fields the site needs (code + locale config) are kept; the display name is derived at render time.
 */
export function registryFromCountryConfigs(list: readonly CountryConfigDTO[]): SiteRegistry {
  return {
    countries: list.map((c) => ({
      code: c.country.toUpperCase(),
      locales: { defaultLocale: c.locales.default, supportedLocales: c.locales.supported },
    })),
  };
}

/** An empty registry — the neutral bootstrap state before the platform injects real country data. */
export const EMPTY_REGISTRY: SiteRegistry = { countries: [] };

const codeEq = (a: string, b: string): boolean => a.toUpperCase() === b.toUpperCase();

/** Every served country code (upper-case), in registry order. */
export function supportedCountryCodes(reg: SiteRegistry): readonly string[] {
  return reg.countries.map((c) => c.code.toUpperCase());
}

/** Whether a country is a served market (has an entry in the registry). */
export function isSupportedCountry(reg: SiteRegistry, code: string): boolean {
  return reg.countries.some((c) => codeEq(c.code, code));
}

/** The registry entry for a country, or `undefined` when it is not served. */
export function countryEntry(reg: SiteRegistry, code: string): SiteCountry | undefined {
  return reg.countries.find((c) => codeEq(c.code, code));
}

/** The locale config for a country, or `undefined` when it is not served. */
export function countryLocales(reg: SiteRegistry, code: string): CountryLocaleConfig | undefined {
  return countryEntry(reg, code)?.locales;
}
